/**
 * The icon-tile stat card used by the Box Truck staff views (Drivers, Bid Analytics) and Mobile OTA Updates.
 *
 * Not to be confused with the same-named tiles in `dat-token-usage` and `crm/DeveloperPanel` — those are
 * visually different components (a bare `Card` with an uppercase label, and a colour-prop variant), not
 * copies of this one. The pages that share this layout must not drift.
 */
import { ReactNode } from 'react'
import Box from '@mui/joy/Box'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'

interface StatCardProps {
    icon: ReactNode
    label: string
    value: ReactNode
    /** Appended after the label as `label · hint`. */
    hint?: string
    /** Joy palette key for the icon tile — lets a card carry a verdict (e.g. coverage). Defaults to primary. */
    tone?: string
}

export function StatCard({ icon, label, value, hint, tone = 'primary' }: StatCardProps) {
    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: 2, display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 170, flex: 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'md',
                    bgcolor: `${tone}.softBg`,
                    color: `${tone}.500`,
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography level="h4" sx={{ lineHeight: 1.1 }}>
                    {value}
                </Typography>
                <Typography level="body-xs" textColor="text.tertiary">
                    {label}
                    {hint ? ` · ${hint}` : ''}
                </Typography>
            </Box>
        </Sheet>
    )
}

/** Local-time timestamp, em-dash for anything missing. Shared so the two staff tables read identically. */
export const fmtDate = (value?: string | null): string => (value ? new Date(value).toLocaleString() : '—')
